import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  TASK_EFFORT_EVENTS,
  TASK_EFFORT_PROPERTIES,
} from '@/constants/analytics/task-effort-events';
import { Task } from '@/services/types/client-intake-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { usePostHogCapture } from './usePostHogCapture';

interface TaskEffortSession {
  taskId: string;
  sessionId: string;
  startTime: number;
}

const STORAGE_KEY_PREFIX = 'task_effort_session_';
const SESSION_EXPIRY_MS = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

/**
 * Get session from sessionStorage for a specific task.
 * Returns null if session doesn't exist or has expired (older than 12 hours).
 */
function getStoredSession(taskId: string): TaskEffortSession | null {
  try {
    const stored = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${taskId}`);
    if (!stored) return null;

    const session = JSON.parse(stored) as TaskEffortSession;

    // Check if session has expired (older than 12 hours)
    const sessionAge = Date.now() - session.startTime;
    if (sessionAge > SESSION_EXPIRY_MS) {
      // Session expired - clear it and return null
      clearStoredSession(taskId);
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

/**
 * Store session in sessionStorage for cross-page persistence
 */
function storeSession(session: TaskEffortSession): void {
  try {
    sessionStorage.setItem(
      `${STORAGE_KEY_PREFIX}${session.taskId}`,
      JSON.stringify(session),
    );
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear session from sessionStorage
 */
function clearStoredSession(taskId: string): void {
  try {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${taskId}`);
  } catch {
    // Ignore storage errors
  }
}

export const useTaskEffortTracking = (task: Task | undefined) => {
  const capture = usePostHogCapture();
  const user = useSelector(selectUser);

  // Start tracking session when task is viewed (only if no existing session)
  useEffect(() => {
    if (!task || !user) {
      return undefined;
    }

    const isAssignedToViewer = task.assignee?.id === user.id;

    // Check if we already have a session for this task (from a previous page)
    const existingSession = getStoredSession(task.id);
    if (existingSession) {
      // Session exists - don't emit task_viewed again, just keep using the existing session
      return undefined;
    }

    // No existing session - create a new one
    const sessionId = `${task.id}-${Date.now()}`;
    const newSession: TaskEffortSession = {
      taskId: task.id,
      sessionId,
      startTime: Date.now(),
    };

    // Store in sessionStorage for cross-page persistence
    storeSession(newSession);

    // Emit task_viewed event
    capture(TASK_EFFORT_EVENTS.TASK_VIEWED, {
      [TASK_EFFORT_PROPERTIES.TASK_ID]: task.id,
      [TASK_EFFORT_PROPERTIES.TASK_TYPE]: task.type?.name,
      [TASK_EFFORT_PROPERTIES.TASK_CANONICAL_TYPE]: task.type?.canonical_type,
      [TASK_EFFORT_PROPERTIES.TASK_GROUP]: task.group?.name,
      [TASK_EFFORT_PROPERTIES.TASK_CANONICAL_GROUP]:
        task.group?.canonical_group,
      [TASK_EFFORT_PROPERTIES.MATTER_ID]: task.matter,
      [TASK_EFFORT_PROPERTIES.ASSIGNEE_ID]: task.assignee?.id,
      [TASK_EFFORT_PROPERTIES.IS_ASSIGNED_TO_VIEWER]: isAssignedToViewer,
      [TASK_EFFORT_PROPERTIES.CURRENT_STATUS]: task.status?.canonical_status,
      [TASK_EFFORT_PROPERTIES.SESSION_ID]: sessionId,
      page: 'tasks',
      component: 'task_detail',
    });

    return undefined;
  }, [task?.id, user?.id, capture, task, user]);

  // Track status update — only emits if user is the assignee and status actually changed
  const trackStatusUpdate = useCallback(
    (previousStatus: string, newStatus: string) => {
      // Check assignee dynamically (not from session) so it's always current
      const isAssignedToViewer = task?.assignee?.id === user?.id;

      if (!task) {
        return;
      }

      // Get session from sessionStorage (persists across page navigations)
      const session = getStoredSession(task.id);

      // Guard: only track if we have a valid session, user is the assignee, and status actually changed
      if (!session) {
        return;
      }
      if (!isAssignedToViewer) {
        return;
      }
      if (previousStatus === newStatus) {
        return;
      }

      const timeToUpdateSeconds = Math.round(
        (Date.now() - session.startTime) / 1000,
      );

      capture(TASK_EFFORT_EVENTS.TASK_STATUS_UPDATED, {
        [TASK_EFFORT_PROPERTIES.TASK_ID]: task.id,
        [TASK_EFFORT_PROPERTIES.TASK_TYPE]: task.type?.name,
        [TASK_EFFORT_PROPERTIES.TASK_CANONICAL_TYPE]: task.type?.canonical_type,
        [TASK_EFFORT_PROPERTIES.TASK_GROUP]: task.group?.name,
        [TASK_EFFORT_PROPERTIES.TASK_CANONICAL_GROUP]:
          task.group?.canonical_group,
        [TASK_EFFORT_PROPERTIES.MATTER_ID]: task.matter,
        [TASK_EFFORT_PROPERTIES.ASSIGNEE_ID]: task.assignee?.id,
        [TASK_EFFORT_PROPERTIES.PREVIOUS_STATUS]: previousStatus,
        [TASK_EFFORT_PROPERTIES.NEW_STATUS]: newStatus,
        [TASK_EFFORT_PROPERTIES.TIME_TO_UPDATE_SECONDS]: timeToUpdateSeconds,
        [TASK_EFFORT_PROPERTIES.SESSION_ID]: session.sessionId,
        [TASK_EFFORT_PROPERTIES.IS_ASSIGNED_TO_VIEWER]: isAssignedToViewer,
        page: 'tasks',
        component: 'task_status_badge',
      });

      // Clear session after tracking (one status update per session)
      clearStoredSession(task.id);
    },
    [task, user, capture],
  );

  return { trackStatusUpdate };
};
